import React, { useState } from 'react';
import { EventListItem } from '../../../types';
import { formatEventDateRange } from '../../../utils/dateFormatters';
import { useGeneral } from '../../../contexts';
import GrayBanner from '../../ui/GrayBanner/GrayBanner';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import styles from './PopularEventCard.module.css';

interface PopularEventCardProps {
  event: EventListItem;
  onClick?: (event: EventListItem) => void;
}

const AVATAR_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
];

const getColorForName = (name: string): string => {
  const index = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const PopularEventCard: React.FC<PopularEventCardProps> = ({
  event,
  onClick
}) => {
  const { language } = useGeneral();
  const { getTranslation, language: currentLanguage } = language;
  const eventTitle = (event.eventTitle || event.title || '').replace(/\\"/g, '"');
  const imageUrl = event.cover || event.bannerImage || event.image || null;
  const hasImage = !!imageUrl;
  const location = event.isOnline === "1" 
    ? getTranslation('events.onlineEvent')
    : event.locationAddress || getTranslation('events.locationTBD');
  const participantCount = Number(event.userCount) || Number(event.user_count) || Number(event.participantsCount) || 0;
  const [imageLoading, setImageLoading] = useState(hasImage);
  
  // Get first 3 users and filter out invalid/empty users
  const first3Users = (event.first3UserIDs || []).filter(user => {
    const displayName = `${user.name || ''} ${user.lastname || ''}`.trim();
    return displayName.length > 0 && user.id;
  });
  
  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  // Format date - replace : with . for time separator
  const formattedDate = formatEventDateRange(event.eventStartDateTime, event.eventEndDateTime, currentLanguage).replace(/:/g, '.');

  return (
    <div 
      className={styles.popularEventCard}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Image */}
      <div className={styles.imageContainer}>
        {hasImage ? (
          <>
            {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
            <img 
              src={imageUrl} 
              alt={eventTitle}
              className={styles.eventImage}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <GrayBanner className={styles.eventImage} id={event.eventID} />
        )}
      </div>
      
      {/* Bottom Section */}
      <div className={styles.bottomSection}>
        <div className={styles.eventDate}>{formattedDate}</div>
        <h4 className={styles.eventTitle}>
          {eventTitle}
        </h4>
        <div className={styles.eventLocation}>{location}</div>
        
        {/* Participant Avatars - spacer always rendered for consistent card height */}
        <div className={styles.participantsSpacer}>
          {first3Users.length > 0 && participantCount > 0 && (
            <div className={styles.participantsSection}>
              <div className={styles.avatarsContainer}>
                {first3Users.slice(0, 3).map((user, index) => {
                  const displayName = `${user.name} ${user.lastname}`.trim();
                  const photoUrl = user.userAvatar;

                  return (
                    <div
                      key={user.id}
                      className={styles.avatarWrapper}
                      style={{ zIndex: 5 - index }}
                    >
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={displayName}
                          className={styles.avatar}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = parent.querySelector(`.${styles.avatarFallback}`);
                              if (fallback) {
                                (fallback as HTMLElement).style.display = 'flex';
                              }
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={styles.avatarFallback}
                        style={{
                          display: photoUrl ? 'none' : 'flex',
                          backgroundColor: getColorForName(displayName),
                          color: '#ffffff'
                        }}
                      >
                        <ChannelInitialIcon
                          channelName={displayName}
                          size={28}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <span className={styles.participantCount}>
                {participantCount} {getTranslation('events.participant') || 'katılımcı'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopularEventCard;

